// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { interviewAPI, workspaceAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search, Plus, Loader2, Trash2, Edit, Mail, Phone, Briefcase,
  Calendar, Users, UserPlus, ChevronRight, Filter, ChevronLeft
} from "lucide-react";
import { toast } from "sonner";
import { getInitials } from "@/lib/utils";

const PIPELINE_LABELS = { to_plan: "À planifier", planned: "Planifié", completed: "Réalisé", validated: "Validé", rejected: "Rejeté", on_hold: "En attente" };
const PIPELINE_COLORS = { to_plan: "bg-gray-100 text-gray-700", planned: "bg-blue-100 text-blue-700", completed: "bg-purple-100 text-purple-700", validated: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700", on_hold: "bg-yellow-100 text-yellow-700" };
const LEVEL_LABELS = { junior: "Junior", confirmed: "Confirmé", senior: "Senior", expert: "Expert" };
const SOURCE_LABELS = { linkedin: "LinkedIn", cooptation: "Cooptation", cabinet: "Cabinet", candidature_spontanee: "Candidature spontanée", autre: "Autre" };

export default function InterviewCandidatesPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [participantDialog, setParticipantDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", experience_years: 0, estimated_level: "confirmed",
    target_role: "", source: "autre", notes: "",
  });

  useEffect(() => { loadData(); }, [workspaceId, filterStatus]);

  const loadData = async () => {
    try {
      const res = await interviewAPI.listCandidates(workspaceId, { pipeline_status: filterStatus, search });
      setCandidates(res.data);
    } catch (e) { toast.error("Erreur"); } finally { setLoading(false); }
  };

  const loadParticipants = async () => {
    try {
      const res = await workspaceAPI.listParticipants(workspaceId);
      setParticipants(res.data);
      setParticipantDialog(true);
    } catch (e) { toast.error("Erreur"); }
  };

  const handleCreate = async () => {
    if (!form.full_name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      await interviewAPI.createCandidate(workspaceId, user?.id, form);
      toast.success("Candidat ajouté");
      setDialogOpen(false);
      loadData();
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const handleAddFromParticipant = async (participantId) => {
    try {
      await interviewAPI.createCandidateFromParticipant(participantId, workspaceId, user?.id);
      toast.success("Candidat créé depuis le participant");
      setParticipantDialog(false);
      loadData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDelete = async () => {
    try {
      await interviewAPI.deleteCandidate(deleteId);
      toast.success("Candidat supprimé");
      setDeleteId(null);
      loadData();
    } catch (e) { toast.error("Erreur"); }
  };

  const pipelineCounts = candidates.reduce((acc, c) => { acc[c.pipeline_status] = (acc[c.pipeline_status] || 0) + 1; return acc; }, {});

  if (loading && candidates.length === 0) {
    return <WorkspaceLayout><div className="page-container"><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div></WorkspaceLayout>;
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/interviews`)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Candidats</h1>
              <p className="mt-1 text-muted-foreground">{candidates.length} candidat(s)</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={loadParticipants}>
              <Users className="h-4 w-4" /> Depuis Participants
            </Button>
            <Button className="gap-2" onClick={() => { setForm({ full_name: "", email: "", phone: "", experience_years: 0, estimated_level: "confirmed", target_role: "", source: "autre", notes: "" }); setDialogOpen(true); }}>
              <UserPlus className="h-4 w-4" /> Nouveau candidat
            </Button>
          </div>
        </div>

        {/* Pipeline status filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Badge variant={filterStatus === "" ? "default" : "outline"} className="cursor-pointer" onClick={() => setFilterStatus("")}>
            Tous ({candidates.length})
          </Badge>
          {Object.entries(PIPELINE_LABELS).map(([k, v]) => (
            <Badge key={k} variant={filterStatus === k ? "default" : "outline"} className={`cursor-pointer ${filterStatus === k ? "" : PIPELINE_COLORS[k]}`}
              onClick={() => setFilterStatus(filterStatus === k ? "" : k)}>
              {v} ({pipelineCounts[k] || 0})
            </Badge>
          ))}
        </div>

        {/* Search */}
        <div className="mt-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Rechercher un candidat..." value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadData()} className="pl-10" />
        </div>

        {/* Candidates List */}
        <div className="mt-6 space-y-3">
          {candidates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Users className="h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 font-semibold">Aucun candidat</h3>
                <p className="mt-2 text-sm text-muted-foreground">Ajoutez un candidat ou importez depuis les participants</p>
              </CardContent>
            </Card>
          ) : (
            candidates.map((c) => (
              <Card key={c.id} className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/workspace/${workspaceId}/interviews/candidates/${c.id}`)}>
                <CardContent className="flex items-center gap-4 p-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {getInitials(c.full_name?.split(" ")[0], c.full_name?.split(" ").slice(1).join(" "))}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{c.full_name}</p>
                      <Badge className={PIPELINE_COLORS[c.pipeline_status] || ""}>{PIPELINE_LABELS[c.pipeline_status] || c.pipeline_status}</Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      {c.target_role && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{c.target_role}</span>}
                      {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                      {c.estimated_level && <Badge variant="outline" className="text-xs">{LEVEL_LABELS[c.estimated_level]}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="text-sm text-muted-foreground">{(c.interviews || []).length} entretien(s)</span>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create Candidate Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouveau candidat</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Nom complet *</Label><Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Poste visé</Label><Input value={form.target_role} onChange={(e) => setForm({...form, target_role: e.target.value})} placeholder="DevOps Engineer" /></div>
              <div><Label>Expérience (ans)</Label><Input type="number" value={form.experience_years} onChange={(e) => setForm({...form, experience_years: parseInt(e.target.value) || 0})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Niveau estimé</Label>
                <Select value={form.estimated_level} onValueChange={(v) => setForm({...form, estimated_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(LEVEL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({...form, source: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Créer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add from Participants Dialog */}
      <Dialog open={participantDialog} onOpenChange={setParticipantDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Ajouter depuis les Participants</DialogTitle></DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-2 mt-2">
            {participants.filter(p => !candidates.some(c => c.participant_id === p.id)).map(p => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border hover:border-primary/50 transition-colors">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary/10 text-primary text-sm">{getInitials(p.first_name, p.last_name)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{p.first_name} {p.last_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                </div>
                <Button size="sm" onClick={() => handleAddFromParticipant(p.id)}><Plus className="h-4 w-4" /></Button>
              </div>
            ))}
            {participants.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Aucun participant dans ce workspace</p>}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Supprimer ce candidat ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WorkspaceLayout>
  );
}
